import { useLocalSearchParams } from 'expo-router'
import { useEffect, useState } from 'react'
import { Platform, View } from 'react-native'
import { useClickOutside } from 'react-native-click-outside'
import { Portal } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import UniButton from '@/components/unistyles/UniButton'
import UniIconButton from '@/components/unistyles/UniIconButton'
import UniText from '@/components/unistyles/UniText'
import formatDate from '@/utils/formatDate'

interface Props {
  isSaving: boolean
  updatedAt?: Date | null
  updatedBy?: string | null
}

const SaveStatus = ({ isSaving, updatedAt, updatedBy }: Props) => {
  const [detailsVisible, setDetailsVisible] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const params = useLocalSearchParams<{ id: string }>()
  const id = params.id || ''
  const ref = useClickOutside<View>(() => setDetailsVisible(false))

  useEffect(() => {
    setHasSaved(false)
  }, [id])

  useEffect(() => {
    if (isSaving) {
      setHasSaved(true)
    }
  }, [isSaving])

  return (
    <View style={styles.root}>
      {isSaving || hasSaved ? (
        <UniButton
          icon="cloud-check-outline"
          loading={isSaving}
          onPress={() => setDetailsVisible(!detailsVisible)}
          style={styles.button}
          testID="save-status-button"
        >
          {isSaving ? 'Saving...' : 'Saved!'}
        </UniButton>
      ) : (
        <UniIconButton
          icon="cloud-check-outline"
          onPress={() => setDetailsVisible(!detailsVisible)}
          testID="save-status-button"
        />
      )}

      {detailsVisible && (
        <Portal>
          <View ref={ref} style={styles.content}>
            {isSaving ? (
              <UniText style={styles.text}>Save in progress...</UniText>
            ) : (
              <>
                {hasSaved && (
                  <UniText style={[styles.text, styles.mb]}>
                    All changes have been saved.
                  </UniText>
                )}
                <UniText style={styles.text}>
                  {!updatedBy && !updatedAt ? (
                    <>No history available.</>
                  ) : (
                    <>
                      Last updated {updatedBy && <>by {updatedBy}</>} on{' '}
                      {formatDate(updatedAt, 'M/dd/yyyy, h:mm a')}
                    </>
                  )}
                </UniText>
              </>
            )}
          </View>
        </Portal>
      )}
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  button: {
    minWidth: 110,
  },
  content: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.tokens.containerBorderRadius,
    padding: theme.tokens.spacing[4],
    position: 'absolute',
    right: 10,
    top: 75,
    width: 300,
    ...Platform.select({
      ios: {
        shadowColor: theme.colors.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  mb: {
    marginBottom: theme.tokens.spacing[4],
  },
  root: {
    position: 'relative',
  },
  text: {
    textAlign: 'center',
  },
}))

export default SaveStatus
